/**
 * Reading QR codes off a camera stream.
 *
 * The stream itself comes from utils/camera.js, the same one the checklist
 * photos use. This file only decodes frames.
 *
 * The browser's own `BarcodeDetector` is used when it exists and reads QR codes
 * (Chrome on Android does, through Play services). Everywhere else the
 * `barcode-detector` ponyfill takes over: same API, backed by ZXing compiled to
 * WebAssembly. It is the decoder `vue-qrcode-reader` used, so nothing changes in
 * what can be read, and it is loaded only the first time a scanner opens.
 */

let detector_promise = null

async function createDetector () {
  const Native = typeof window !== 'undefined' ? window.BarcodeDetector : undefined
  if (Native) {
    try {
      const formats = await Native.getSupportedFormats()
      if (formats.includes('qr_code')) return new Native({ formats: ['qr_code'] })
    } catch (e) { /* fall through to the ponyfill */ }
  }
  const [{ BarcodeDetector, setZXingModuleOverrides }, { default: wasmUrl }] = await Promise.all([
    import('barcode-detector/pure'),
    import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ])
  // The decoder from our own build. By default ZXing fetches it from
  // jsDelivr, which a factory network or an offline tablet may not reach.
  setZXingModuleOverrides({
    locateFile: (path, prefix) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
  })
  return new BarcodeDetector({ formats: ['qr_code'] })
}

/** One detector for the whole session: building one loads the decoder. */
export function getQrDetector () {
  if (!detector_promise) {
    detector_promise = createDetector().catch((e) => {
      detector_promise = null
      throw e
    })
  }
  return detector_promise
}

/** The text of the first QR code in the current frame of `video`, or "". */
export async function readQr (video) {
  if (!video || !video.videoWidth || !video.videoHeight) return ''
  const detector = await getQrDetector()
  const codes = await detector.detect(video)
  return codes?.[0]?.rawValue || ''
}
